/**
 * Shared rendering components for Schedule Variance (SPI) and Budget Variance (CPI).
 *
 * Extracted from ScheduleVariance.tsx and BudgetVariance.tsx to eliminate ~800 lines
 * of near-identical duplication. Domain-specific data loading remains in each consumer;
 * all chart rendering, mini rows, and modal overlays are handled here.
 *
 * @module varianceChartShared
 */

import React, { useRef, useState } from "react";
import { useAppTranslation } from '../../i18n/useAppTranslation';
import styles from "./Navigation.module.css";
import { formatFixed } from '../../utils/numberFormat';
import { toISODate, parseISODateToMs, clamp01 } from './thermometerUtils';

// ────────────────────────────────────────────────
// Color constants & band-to-color mapper
// ────────────────────────────────────────────────

const GREEN = "#2ecc71";
const YELLOW = "#f1c40f";
const RED = "#e74c3c";

/** Map a tolerance band (0–3) to a hex color string. Identical for SPI and CPI. */
export function indexColorHex(band: 0 | 1 | 2 | 3): string {
  if (band === 1) return RED;
  if (band === 2) return YELLOW;
  if (band === 3) return GREEN;
  return "var(--text-muted)";
}

// ────────────────────────────────────────────────
// Generic Performance-Index History Chart
// ────────────────────────────────────────────────

/** A single history data-point, normalized for the shared chart. */
export type VarianceHistoryPoint = {
  date: string;
  value: number;
  belowGuard?: boolean;
};

type HistoryChartProps = {
  data: VarianceHistoryPoint[];
  warningThreshold: number;
  criticalThreshold: number;
  asOfDate: string;
  /** Color for the as-of (today) vertical line — matches current KPI band color */
  asOfColor?: string;
  /** Full project timespan start (ISO date) — aligns x-axis with Progress chart */
  fullDateRangeMin?: string;
  /** Full project timespan end (ISO date) — aligns x-axis with Progress chart */
  fullDateRangeMax?: string;
  /** Diagnostic message when data is empty */
  emptyReason?: string;
  /** Watermark label shown at the top of the chart area (e.g. "AHEAD", "UNDER") */
  watermarkHigh: string;
  /** Watermark label shown at the bottom of the chart area (e.g. "BEHIND", "OVER") */
  watermarkLow: string;
  /** Label shown in the hover tooltip (e.g. "SPI", "CPI") */
  tooltipLabel: string;
};

/**
 * Generic performance-index history chart.
 * Renders colored bands, threshold lines, a colored data line with segments,
 * hover crosshair + tooltip, and watermark labels.
 *
 * Used by both ScheduleVariance (SPI) and BudgetVariance (CPI).
 */
export function PerformanceIndexHistoryChart({
  data,
  warningThreshold,
  criticalThreshold,
  asOfDate,
  asOfColor,
  fullDateRangeMin,
  fullDateRangeMax,
  emptyReason,
  watermarkHigh,
  watermarkLow,
  tooltipLabel,
}: HistoryChartProps) {
  const { t } = useAppTranslation('kpi');
  // --- Hover / crosshair state (must be before any early return) ---
  const svgRef = useRef<SVGSVGElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [hoverClient, setHoverClient] = useState<{ x: number; y: number } | null>(null);

  if (!data.length) {
    return (
      <div
        style={{
          flex: "1 1 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--text-tertiary)",
          fontSize: 13,
          flexDirection: "column",
          gap: 6,
          textAlign: "center",
          padding: "12px 20px",
        }}
      >
        <div>{t('variance.awaitingData')}</div>
        {emptyReason ? (
          <div style={{ fontSize: 11, opacity: 0.75, lineHeight: 1.4 }}>{emptyReason}</div>
        ) : null}
      </div>
    );
  }

  // Dimensions — match Progress chart viewBox for identical text stretch
  const W = 860;
  const H = 220;
  const padL = 44;
  const padR = 12;
  const padT = 12;
  const padB = 34;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  // X axis: full project timespan (aligns with Progress chart)
  const dataMs = data.map((p) => parseISODateToMs(p.date));
  const msMin = fullDateRangeMin
    ? Math.min(parseISODateToMs(fullDateRangeMin), ...dataMs)
    : Math.min(...dataMs);
  const msMax = fullDateRangeMax
    ? Math.max(parseISODateToMs(fullDateRangeMax), ...dataMs)
    : Math.max(...dataMs);
  const msRange = msMax - msMin || 1;

  const xFor = (date: string) => {
    const ms = parseISODateToMs(date);
    const frac = (ms - msMin) / msRange;
    return padL + clamp01(frac) * innerW;
  };

  // Y axis: auto-range around the data, centered on 1.0
  const allValues = data.map((p) => p.value).filter(Number.isFinite);
  const dataMin = allValues.length ? Math.min(...allValues) : 0.8;
  const dataMax = allValues.length ? Math.max(...allValues) : 1.2;
  // Ensure 1.0 is always visible and give 10% breathing room
  const rawMin = Math.min(dataMin, criticalThreshold - 0.05, 0.8);
  const rawMax = Math.max(dataMax, warningThreshold + 0.05, 1.2);
  const yRange = rawMax - rawMin || 0.4;
  const yMin = rawMin - yRange * 0.08;
  const yMax = rawMax + yRange * 0.08;

  const yFor = (val: number) => {
    const frac = (val - yMin) / (yMax - yMin);
    return padT + (1 - Math.max(0, Math.min(1, frac))) * innerH;
  };

  // Colored horizontal bands — very subtle tints only
  const bands = [
    { y1: yFor(Math.min(yMax, 2)), y2: yFor(warningThreshold), fill: "rgba(34,197,94,0.04)" },
    { y1: yFor(warningThreshold), y2: yFor(criticalThreshold), fill: "rgba(245,158,11,0.05)" },
    { y1: yFor(criticalThreshold), y2: yFor(Math.max(yMin, 0)), fill: "rgba(239,68,68,0.04)" },
  ];

  // Line — colored segments based on band (green/yellow/red).
  const YELLOW_SEG = "#f1c40f";
  const RED_SEG = "#e74c3c";
  const segColor = (val: number) =>
    val >= warningThreshold ? GREEN : val >= criticalThreshold ? YELLOW_SEG : RED_SEG;

  const varianceSegments: Array<{
    x1: number; y1: number; x2: number; y2: number;
    color: string; guard: boolean;
  }> = [];
  for (let i = 1; i < data.length; i++) {
    const prev = data[i - 1];
    const curr = data[i];
    varianceSegments.push({
      x1: xFor(prev.date), y1: yFor(prev.value),
      x2: xFor(curr.date), y2: yFor(curr.value),
      color: segColor(curr.value),
      guard: !!curr.belowGuard,
    });
  }

  // As-of marker
  const asOfMs = parseISODateToMs(asOfDate);
  const showAsOf = asOfMs >= msMin && asOfMs <= msMax;
  const asOfX = xFor(asOfDate);

  // Grid lines — dynamic based on visible range
  const gridValues: number[] = [];
  const gridStep = yMax - yMin > 0.6 ? 0.25 : 0.1;
  for (let v = Math.ceil(yMin / gridStep) * gridStep; v <= yMax; v += gridStep) {
    gridValues.push(Math.round(v * 100) / 100);
  }
  // Always include 1.0
  if (!gridValues.includes(1.0)) gridValues.push(1.0);
  gridValues.sort((a, b) => a - b);

  const nearestIndexForMs = (ms: number): number => {
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < dataMs.length; i++) {
      const d = Math.abs(dataMs[i] - ms);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    return best;
  };

  const onMouseMove: React.MouseEventHandler<SVGSVGElement> = (e) => {
    if (!svgRef.current || !data.length) return;
    const rect = svgRef.current.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const svgX = (px / rect.width) * W;
    const frac = clamp01((svgX - padL) / innerW);
    const ms = msMin + frac * msRange;
    const idx = nearestIndexForMs(ms);
    setHoverIndex(idx);
    setHoverClient({ x: e.clientX, y: e.clientY });
  };

  const onMouseLeave: React.MouseEventHandler<SVGSVGElement> = () => {
    setHoverIndex(null);
    setHoverClient(null);
  };

  const hoverPoint = hoverIndex !== null ? data[hoverIndex] : null;

  return (
    <div ref={wrapRef} style={{ width: "100%", height: "100%", position: "relative", overflow: "hidden" }}>
      <svg
        ref={svgRef}
        className={styles.scheduleChartSvg}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        style={{ cursor: data.length ? "crosshair" : undefined }}
      >
        {/* Colored bands */}
        {bands.map((b, i) => (
          <rect
            key={i}
            x={padL}
            y={b.y1}
            width={innerW}
            height={Math.max(0, b.y2 - b.y1)}
            fill={b.fill}
          />
        ))}

        {/* Grid lines */}
        {gridValues.map((v) => (
          <g key={v}>
            <line
              x1={padL}
              x2={padL + innerW}
              y1={yFor(v)}
              y2={yFor(v)}
              stroke={
                v === 1.0
                  ? "var(--border-strong)"
                  : "var(--border-subtle)"
              }
              strokeWidth={v === 1.0 ? 1.5 : 0.5}
              strokeDasharray={v === 1.0 ? undefined : "4 4"}
            />
            <text
              x={padL - 6}
              y={yFor(v) + 4}
              textAnchor="end"
              className={styles.scheduleChartTickText}
            >
              {formatFixed(v, v === 1.0 ? 1 : Math.abs(v) >= 10 ? 1 : 2)}
            </text>
          </g>
        ))}

        {/* Threshold lines */}
        <line
          x1={padL}
          x2={padL + innerW}
          y1={yFor(warningThreshold)}
          y2={yFor(warningThreshold)}
          stroke={YELLOW}
          strokeWidth={1}
          strokeDasharray="6 3"
          opacity={0.5}
        />
        <line
          x1={padL}
          x2={padL + innerW}
          y1={yFor(criticalThreshold)}
          y2={yFor(criticalThreshold)}
          stroke={RED}
          strokeWidth={1}
          strokeDasharray="6 3"
          opacity={0.5}
        />

        {/* As-of marker — colored to match current KPI band color */}
        {showAsOf ? (
          <line
            x1={asOfX}
            x2={asOfX}
            y1={padT}
            y2={padT + innerH}
            stroke={asOfColor || "var(--border-strong)"}
            strokeWidth={1.5}
            strokeDasharray="6,4"
            opacity={0.7}
          />
        ) : null}

        {/* Watermark text — subtle background labels */}
        <text
          x={padL + innerW - 10}
          y={padT + 30}
          textAnchor="end"
          fontSize="32"
          fontWeight="400"
          letterSpacing="2"
          fill="var(--text-muted)"
        >
          {watermarkHigh}
        </text>
        <text
          x={padL + innerW - 10}
          y={padT + innerH - 8}
          textAnchor="end"
          fontSize="32"
          fontWeight="400"
          letterSpacing="2"
          fill="var(--text-muted)"
        >
          {watermarkLow}
        </text>

        {/* Index line — colored segments by band (green/yellow/red) */}
        <g>
          {varianceSegments.map((seg, idx) => (
            <line
              key={`idx-seg-${idx}`}
              x1={seg.x1}
              y1={seg.y1}
              x2={seg.x2}
              y2={seg.y2}
              stroke={seg.color}
              strokeWidth={seg.guard ? 2 : 2.5}
              strokeLinecap="round"
              strokeDasharray={seg.guard ? "4 3" : undefined}
              opacity={seg.guard ? 0.5 : 1}
            />
          ))}
        </g>

        {/* Data points — colored by band */}
        {data.map((p, i) => {
          const x = xFor(p.date);
          const y = yFor(p.value);
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={p.belowGuard ? 2 : 2.5}
              fill={segColor(p.value)}
              stroke="var(--bg-secondary)"
              strokeWidth={1}
              opacity={p.belowGuard ? 0.5 : 1}
            />
          );
        })}

        {/* Hover crosshair + highlighted dot */}
        {hoverPoint ? (() => {
          const hx = xFor(hoverPoint.date);
          const hy = yFor(hoverPoint.value);
          return (
            <>
              <line
                x1={hx}
                y1={padT}
                x2={hx}
                y2={padT + innerH}
                className={styles.scheduleChartHoverLine}
              />
              <circle
                cx={hx}
                cy={hy}
                r={4}
                className={styles.scheduleChartDot}
              />
            </>
          );
        })() : null}

        {/* Border frame */}
        <rect
          x={padL}
          y={padT}
          width={innerW}
          height={innerH}
          fill="none"
          stroke="var(--border-subtle)"
          strokeWidth={1}
        />
      </svg>

      {/* Tooltip */}
      {hoverPoint && hoverClient ? (
        <div
          className={styles.scheduleTooltip}
          style={{
            insetInlineStart: hoverClient.x + 12,
            top: hoverClient.y + 12,
          }}
        >
          <div className={styles.scheduleTooltipDate}>
            {hoverPoint.date}
          </div>
          <div className={styles.scheduleTooltipRow}>
            <span className={styles.scheduleTooltipLabel}>{tooltipLabel}</span>
            <span className={styles.scheduleTooltipValue}>
              {Number.isFinite(hoverPoint.value) ? formatFixed(hoverPoint.value, Math.abs(hoverPoint.value) >= 10 ? 2 : 3) : "—"}
            </span>
          </div>
          {hoverPoint.belowGuard ? (
            <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>
              {t('variance.earlyMaturity')}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ────────────────────────────────────────────────
// Mini row — collapsed single-line summary
// ────────────────────────────────────────────────

type MiniRowProps = {
  title: string;
  currentValue: number | null;
  bandColorHex: string;
  bandLabel: string;
  onExpand: () => void;
};

/** Collapsed single-line row with index value + status pill + expand button. */
export function VarianceMiniRow({
  title,
  currentValue,
  bandColorHex,
  bandLabel,
  onExpand,
}: MiniRowProps) {
  const { t } = useAppTranslation('kpi');
  return (
    <div
      className={styles.scheduleChartRow}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "6px 12px",
        cursor: "pointer",
        borderRadius: 8,
        background: "var(--bg-hover)",
        gap: 8,
      }}
      onClick={onExpand}
    >
      {/* Title on left */}
      <span
        style={{
          fontWeight: 700,
          fontSize: 14,
          color: "var(--text-primary)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: "1 1 auto",
          minWidth: 0,
        }}
      >
        {title}
      </span>

      {/* Right group: value + pill + expand */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {currentValue !== null ? (
          <>
            <span
              style={{
                fontWeight: 800,
                fontSize: 15,
                color: bandColorHex,
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
                minWidth: 52,
                textAlign: "right",
              }}
            >
              {formatFixed(currentValue, Math.abs(currentValue) >= 10 ? 1 : 2)}
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 72,
                padding: "3px 0",
                borderRadius: 999,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: 0.3,
                color: bandColorHex,
                textTransform: "uppercase" as const,
                background: `${bandColorHex}40`,
                border: `1px solid ${bandColorHex}80`,
                whiteSpace: "nowrap",
              }}
            >
              {bandLabel}
            </span>
          </>
        ) : (
          <span style={{ fontSize: 13, color: "var(--text-tertiary)" }}>
            No data
          </span>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onExpand();
          }}
          title={t('thermometer.expand')}
          style={{
            background: "none",
            border: "1px solid var(--border-default)",
            color: "var(--text-tertiary)",
            cursor: "pointer",
            borderRadius: 6,
            width: 28,
            height: 28,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            lineHeight: 1,
            padding: 0,
          }}
        >
          +
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────
// Modal overlay — expanded-from-mini full view
// ────────────────────────────────────────────────

type ModalOverlayProps = {
  title: string;
  fullContent: React.ReactNode;
  onCollapse: () => void;
};

/** Full-screen overlay that wraps the expanded variance chart when triggered from mini mode. */
export function VarianceModalOverlay({
  title,
  fullContent,
  onCollapse,
}: ModalOverlayProps) {
  return (
    <>
      {/* Mini row placeholder */}
      <div
        className={styles.scheduleChartRow}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "6px 12px",
          cursor: "pointer",
          borderRadius: 8,
          background: "var(--bg-hover)",
          gap: 8,
        }}
        onClick={onCollapse}
      >
        <span
          style={{
            fontWeight: 700,
            fontSize: 14,
            color: "var(--text-primary)",
          }}
        >
          {title}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onCollapse();
          }}
          title="Collapse"
          style={{
            background: "none",
            border: "1px solid var(--border-default)",
            color: "var(--text-tertiary)",
            cursor: "pointer",
            borderRadius: 6,
            width: 28,
            height: 28,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            lineHeight: 1,
            padding: 0,
          }}
        >
          −
        </button>
      </div>
      {/* Expanded overlay */}
      <div
        style={{
          position: "fixed",
          top: 0,
          insetInline: 0,
          bottom: 0,
          background: "var(--bg-elevated)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 32,
        }}
        onClick={onCollapse}
      >
        <div
          style={{ maxWidth: 900, width: "100%" }}
          onClick={(e) => e.stopPropagation()}
        >
          {fullContent}
        </div>
      </div>
    </>
  );
}
